import { useState, useEffect, useCallback, useMemo } from 'react';
import { getShotVersions, createShotVersion, restoreShotVersion, deleteShotVersion, getFrameImageUrl, getConceptVersions, getConceptImageUrl } from '@/services/shotService';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GitBranch, Save, RotateCcw, Trash2, Eye, X, Loader2, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { useCollaboration } from '@/contexts/CollaborationContext';


const TRIGGER_LABELS = {
  regenerate: "Before Regenerate",
  style_upgrade: "Before Style Upgrade",
  restore: "Before Restore",
  delete: "Before Delete",
};

function VersionCard({ ver, onCompare, onRestore, onDelete, compact }) {
  return (
    <motion.div
      key={ver.version_id}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-muted)]/50 ${compact ? 'p-2' : 'p-2.5'} hover:border-[var(--color-gold-500)]/30 transition-colors`}
      data-testid={`version-item-${ver.version_id}`}
    >
      <div className="flex items-start justify-between mb-1.5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} font-medium text-[var(--text-primary)] truncate`}>{ver.name}</p>
            {ver.trigger && TRIGGER_LABELS[ver.trigger] && (
              <span className="shrink-0 text-[9px] px-1.5 py-0.5 rounded-full bg-[var(--bg-overlay)] text-[var(--text-disabled)]">
                {TRIGGER_LABELS[ver.trigger]}
              </span>
            )}
          </div>
          <p className="text-[10px] text-[var(--text-disabled)]">
            {ver.created_by_name ? `by ${ver.created_by_name} · ` : ''}{new Date(ver.created_at).toLocaleDateString()}
          </p>
        </div>
        <span className="text-[10px] text-[var(--color-gold-400)] font-mono shrink-0 ml-2">v{ver.version_number}</span>
      </div>
      {ver.notes && !compact && (
        <p className="text-[10px] text-[var(--text-muted)] mb-2">{ver.notes}</p>
      )}
      {ver.image_data && !compact && (
        <div
          className="w-full aspect-video rounded bg-black mb-2 cursor-pointer overflow-hidden"
          onClick={() => onCompare(ver)}
        >
          <img src={`data:image/png;base64,${ver.image_data}`} alt={ver.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onCompare(ver)}
          className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-[10px] bg-[var(--bg-overlay)] text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
          data-testid={`compare-version-${ver.version_id}`}
        >
          <Eye size={10} /> Compare
        </button>
        <button
          onClick={() => onRestore(ver.version_id)}
          className="flex-1 flex items-center justify-center gap-1 py-1 rounded text-[10px] bg-[var(--color-gold-500)]/10 text-[var(--color-gold-400)] hover:bg-[var(--color-gold-500)]/20 transition-colors"
          data-testid={`restore-version-${ver.version_id}`}
        >
          <RotateCcw size={10} /> Restore
        </button>
        <button
          onClick={() => onDelete(ver.version_id)}
          className="flex items-center justify-center py-1 px-2 rounded text-[10px] bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
          data-testid={`delete-version-${ver.version_id}`}
        >
          <Trash2 size={10} />
        </button>
      </div>
    </motion.div>
  );
}


export default function ShotVersionPanel({ projectId, frame, concept, onRestored, onClose }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [versionName, setVersionName] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [compareVersion, setCompareVersion] = useState(null);
  const [showAutoSaves, setShowAutoSaves] = useState(false);
  const { emitShotVersion } = useCollaboration();

  // Determine mode: concept-based or frame-based
  const isConceptMode = !!concept;
  const entityId = isConceptMode ? concept?.concept_id : frame?.id;
  const entityLabel = isConceptMode ? concept?.name : `Shot ${frame?.frame_number}`;

  const fetchVersions = useCallback(async () => {
    if (!projectId || !entityId) return;
    setLoading(true);
    try {
      const data = isConceptMode
        ? await getConceptVersions(projectId, entityId)
        : await getShotVersions(projectId, entityId);
      setVersions(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [projectId, entityId, isConceptMode]);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  // Split versions into manual and auto groups
  const { manualVersions, autoVersions } = useMemo(() => {
    const manual = [];
    const auto = [];
    for (const v of versions) {
      if (v.version_type === 'auto') {
        auto.push(v);
      } else {
        manual.push(v);
      }
    }
    return { manualVersions: manual, autoVersions: auto };
  }, [versions]);

  const handleSave = async () => {
    if (!entityId) return;
    if (isConceptMode) {
      // Concept mode: versions are auto-managed by the backend, manual save not yet supported
      toast.info('Versions are automatically saved when you regenerate or edit');
      return;
    }
    setSaving(true);
    try {
      const data = await createShotVersion(projectId, frame.id, {
        name: versionName || undefined,
        notes: versionNotes || undefined,
      });
      toast.success(`Saved as ${data.name}`);
      setVersionName('');
      setVersionNotes('');
      fetchVersions();
      emitShotVersion?.(frame.id, data);
    } catch {
      toast.error('Failed to save version');
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (versionId) => {
    if (isConceptMode) {
      // TODO: Implement concept version restore
      toast.info('Concept version restore coming soon');
      return;
    }
    try {
      await restoreShotVersion(projectId, frame.id, versionId);
      toast.success('Version restored');
      onRestored?.();
    } catch {
      toast.error('Failed to restore version');
    }
  };

  const handleDelete = async (versionId) => {
    if (isConceptMode) {
      // For now, don't allow deleting concept versions
      return;
    }
    try {
      await deleteShotVersion(projectId, frame.id, versionId);
      setVersions(prev => prev.filter(v => v.version_id !== versionId));
      toast.success('Version deleted');
    } catch {
      toast.error('Failed to delete version');
    }
  };

  if (!frame && !concept) return null;

  return (
    <div className="flex flex-col h-full" data-testid="shot-version-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-2">
          <GitBranch size={16} className="text-[var(--color-gold-400)]" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">{isConceptMode ? 'Concept' : 'Shot'} Versions</span>
          <span className="text-xs text-[var(--text-disabled)]">{entityLabel}</span>
        </div>
        {onClose && (
          <button onClick={onClose} className="btn-ghost btn-icon-xs">
            <X size={14} />
          </button>
        )}
      </div>

      {/* Save New Version */}
      <div className="px-4 py-3 border-b border-[var(--border-subtle)] space-y-2">
        <Input
          placeholder="Version name (optional)"
          value={versionName}
          onChange={e => setVersionName(e.target.value)}
          className="input text-xs h-8"
          data-testid="version-name-input"
        />
        <Textarea
          placeholder="Notes..."
          value={versionNotes}
          onChange={e => setVersionNotes(e.target.value)}
          className="input text-xs resize-none h-14"
          data-testid="version-notes-input"
        />
        <Button
          onClick={handleSave}
          disabled={saving || isConceptMode || (frame && frame.status !== "completed")}
          size="sm"
          className="w-full btn-primary text-xs h-8"
          data-testid="save-version-btn"
        >
          {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
          Save Current as Version
        </Button>
      </div>

      {/* Version List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {loading && (
            <div className="flex justify-center py-6">
              <Loader2 size={18} className="animate-spin text-[var(--text-disabled)]" />
            </div>
          )}

          {!loading && versions.length === 0 && (
            <p className="text-xs text-[var(--text-disabled)] text-center py-6">No versions saved yet</p>
          )}

          {/* Saved Versions (manual) */}
          {manualVersions.length > 0 && (
            <>
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wider px-1">
                Saved Versions ({manualVersions.length})
              </p>
              {manualVersions.map(ver => (
                <VersionCard
                  key={ver.version_id}
                  ver={ver}
                  onCompare={setCompareVersion}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                />
              ))}
            </>
          )}

          {/* Auto-saves (collapsible) */}
          {autoVersions.length > 0 && (
            <>
              <button
                onClick={() => setShowAutoSaves(!showAutoSaves)}
                className="w-full flex items-center justify-between px-1 py-1.5 text-[10px] font-semibold text-[var(--text-disabled)] uppercase tracking-wider hover:text-[var(--text-muted)] transition-colors"
              >
                <div className="flex items-center gap-1.5">
                  <Shield size={10} />
                  Auto-saves ({autoVersions.length})
                </div>
                {showAutoSaves ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
              </button>
              {showAutoSaves && autoVersions.map(ver => (
                <VersionCard
                  key={ver.version_id}
                  ver={ver}
                  onCompare={setCompareVersion}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                  compact
                />
              ))}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Compare Dialog */}
      <Dialog open={!!compareVersion} onOpenChange={() => setCompareVersion(null)}>
        <DialogContent className="max-w-4xl bg-[var(--bg-surface)] border-[var(--border-default)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--text-primary)]">
              Compare: Current vs {compareVersion?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-[var(--text-muted)] mb-2 text-center">Current</p>
              <div className="aspect-video rounded-lg bg-black overflow-hidden">
                {isConceptMode && concept?.status === "completed" ? (
                  <img src={getConceptImageUrl(concept.concept_id)} alt="Current" className="w-full h-full object-cover" crossOrigin="use-credentials" />
                ) : frame?.status === "completed" ? (
                  <img src={getFrameImageUrl(frame.id)} alt="Current" className="w-full h-full object-cover" crossOrigin="use-credentials" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--text-disabled)] text-xs">No image</div>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-[var(--text-muted)] mb-2 text-center">{compareVersion?.name} (v{compareVersion?.version_number})</p>
              <div className="aspect-video rounded-lg bg-black overflow-hidden">
                {compareVersion?.image_data ? (
                  <img src={`data:image/png;base64,${compareVersion.image_data}`} alt={compareVersion?.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[var(--text-disabled)] text-xs">No image</div>
                )}
              </div>
            </div>
          </div>
          {compareVersion?.notes && (
            <p className="text-xs text-[var(--text-muted)] mt-2">Notes: {compareVersion.notes}</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
